import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetStartStyled, PopupSetupStyled } from './styled';
import { BlockStack, Button, Collapsible, Icon, InlineStack, Popover, ProgressBar, Spinner, Text } from '@shopify/polaris';
import { StatusActiveIcon } from '@shopify/polaris-icons';
import { ChevronDownIcon } from '@shopify/polaris-icons';
import { images } from '@/asset';
import { useNavigate } from 'react-router-dom';
import { apiCaller } from '@/redux/query';
import { useDispatch, useSelector } from 'react-redux';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { useAppEmbedDeepLink } from '@/hooks/useAppEmbedDeepLink';
import { dataSettingsSelector, loadingSelector } from '@/redux/slice/dataSettings.slice';
import { PATH } from '@/constants';
import settingsSlice from '@/redux/slice/settings.slice';
import { IDScrollIntoView, OptionSettings } from '@/constants/enum';
import {
  handleHasClickGetStared,
  handleShowPopoverGetStared,
  hasClickGetStaredSelector,
  showPopoverGetStaredSelector,
} from '@/redux/slice/home.slice';
import { handleIdScrollIntoView } from '@/redux/slice/home.slice';
import { useTranslation } from 'react-i18next';

enum IDStep {
  Step1 = 'step1',
  Step2 = 'step2',
  Step3 = 'step3',
  Step4 = 'step4',
}

type Step = {
  step: IDStep;
  title: string;
  description: string;
  completed: boolean;
  buttonLabel: string;
  isLoading: boolean;
  onClick: () => void;
};

const GetStarted = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');

  const isSkip = useSelector(isSkipApiSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const isLoadingDataSettings = useSelector(loadingSelector);
  const showPopoverGetStared = useSelector(showPopoverGetStaredSelector);
  const hasClickGetStared = useSelector(hasClickGetStaredSelector);

  const checkEmbeddedStatus = apiCaller.useCheckAppEmbedStatusQuery(undefined, { skip: isSkip });
  const deepLinkUrl = useAppEmbedDeepLink();

  const [collapse, setCollapse] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
  });

  const listSteps: Step[] = useMemo(
    () => [
      {
        step: IDStep.Step1,
        title: t('activate_app_embed'),
        description: t('activate_display_blockify_blocking_page'),
        completed: !!checkEmbeddedStatus?.data?.status,
        buttonLabel: t('go_to_theme_editor'),
        isLoading: checkEmbeddedStatus.isLoading,
        onClick: () => window.open(deepLinkUrl, '_blank'),
      },
      // {
      //   step: IDStep.Step2,
      //   title: t('turn_on_visitor_analytics'),
      //   description: t('track_visitor_access'),
      //   completed: !!dataSettings?.settings.enableVisitorAnalytics,
      //   buttonLabel: t('go_to_visitor_analytics'),
      //   isLoading: isLoadingDataSettings,
      //   onClick: () => navigate(PATH.ANALYTICS),
      // },
      // {
      //   step: IDStep.Step3,
      //   title: t('turn_on_fraud_order_analytics'),
      //   description: t('detect_fraudulent_orders'),
      //   completed: !!dataSettings?.settings.user.orderScope,
      //   buttonLabel: t('go_to_fraud_order_analytics'),
      //   isLoading: isLoadingDataSettings,
      //   onClick: () => navigate(PATH.FRAUD_ORDER),
      // },
      {
        step: IDStep.Step4,
        title: t('turn_on_block_vpn_proxy'),
        description: t('block_vpn_proxy_users'),
        completed: !!dataSettings?.settings.vpnBlocker,
        buttonLabel: t('block_vpn_proxy'),
        isLoading: isLoadingDataSettings,
        onClick: () => {
          dispatch(handleIdScrollIntoView(IDScrollIntoView.Proxy_and_vpn_blocker));
          dispatch(settingsSlice.actions.handleSelected([OptionSettings.BotKiller]));
          navigate(PATH.SETTINGS);
        },
      },
    ],
    [
      checkEmbeddedStatus?.data?.status,
      checkEmbeddedStatus.isLoading,
      dataSettings?.settings.vpnBlocker,
      // dataSettings?.settings.enableVisitorAnalytics,
      // dataSettings?.settings.user.orderScope,
      dispatch,
      isLoadingDataSettings,
      navigate,
      t,
    ],
  );

  const totalCompleted = listSteps.reduce((acc, cur) => {
    const stepCompleted = cur.completed ? 1 : 0;
    return acc + stepCompleted;
  }, 0);

  useEffect(() => {
    if (isLoadingDataSettings || checkEmbeddedStatus.isLoading) return;

    const firstIncompleteStep = listSteps.find((step) => !step.completed)?.step;

    if (firstIncompleteStep && !hasClickGetStared) {
      setCollapse((props) => ({
        ...props,
        [firstIncompleteStep]: true,
      }));
      dispatch(handleShowPopoverGetStared(true));
    }
  }, [checkEmbeddedStatus.isLoading, isLoadingDataSettings, listSteps, hasClickGetStared, dispatch]);

  const handleCollapse = (key: keyof typeof collapse) => () => {
    const result = { ...collapse };
    Object.keys(result).forEach((k) => {
      result[k as keyof typeof result] = false;
    });
    setCollapse({
      ...result,
      [key]: !collapse[key],
    });
  };

  const togglePopoverActive = useCallback(() => {
    dispatch(handleShowPopoverGetStared(!showPopoverGetStared));
    if (!hasClickGetStared) dispatch(handleHasClickGetStared(true));
  }, [dispatch, hasClickGetStared, showPopoverGetStared]);

  const activator = (
    <div className="btn-get-started" onClick={togglePopoverActive}>
      <Text variant="headingMd" as="h2">
        {t('get_started')}
      </Text>
      {isLoadingDataSettings || checkEmbeddedStatus.isLoading ? (
        <div className="d-flex flex-center mr-8">
          <Spinner size="small" />
        </div>
      ) : (
        <Icon source={StatusActiveIcon} tone="base" />
      )}
    </div>
  );

  return (
    <GetStartStyled id="GetStartStyled">
      <Popover activator={activator} autofocusTarget="first-node" onClose={togglePopoverActive} active={showPopoverGetStared}>
        <PopupSetupStyled>
          <BlockStack gap="200">
            <InlineStack blockAlign="center" align="space-between">
              <Text as="h4" variant="headingMd">
                {t('dashboard:set_up_name')}
              </Text>
              <Button onClick={togglePopoverActive} icon={ChevronDownIcon} variant="tertiary" />
            </InlineStack>

            <Text as="p" tone="subdued">
              {t('secure_your_store_steps_2')}
            </Text>

            <InlineStack blockAlign="center" align="space-between">
              <InlineStack gap="200" blockAlign="center" align="space-between">
                <div className="progress-bar-wrap">
                  <ProgressBar progress={(totalCompleted / 2) * 100} size="small" />
                </div>
              </InlineStack>

              <Text as="span">{t('number_completed_2', { number: totalCompleted })}</Text>
            </InlineStack>

            <BlockStack gap="050">
              {listSteps.map((step) => (
                <div
                  className={`set-item ${collapse[step.step] ? 'open-collapse' : ''}`}
                  key={step.step}
                  onClick={handleCollapse(step.step)}
                >
                  <div className="set-item-right">
                    <Button
                      loading={step.isLoading}
                      id="circle"
                      variant="plain"
                      icon={
                        step.completed ? (
                          <img src={images.circleChecked} width={20} alt="circle-tick" />
                        ) : (
                          <div className="circle" />
                        )
                      }
                    />
                  </div>

                  <BlockStack gap="150" inlineAlign="start">
                    <Text as="h5" variant={step.completed || collapse[step.step] ? 'headingMd' : undefined}>
                      {step.title}
                    </Text>
                    <Text as="h6" tone="subdued">
                      {step.description}
                    </Text>

                    <Collapsible
                      open={collapse[step.step]}
                      id=""
                      transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
                      expandOnPrint
                    >
                      <Button onClick={step.onClick} variant="primary">
                        {step.buttonLabel}
                      </Button>
                    </Collapsible>
                  </BlockStack>
                </div>
              ))}
            </BlockStack>
          </BlockStack>
        </PopupSetupStyled>
      </Popover>
    </GetStartStyled>
  );
};

export default GetStarted;
